import React, { useReducer } from "react";
import { MAuswahl } from "../../../metamodell";
import { Image } from "../Image";
import { MinimalNumberInput } from "../../commons/MinimalNumberInput";
import { InfoIcon } from "../NumberSelect";
import * as Commons from "oc-commons/Commons";

export interface AnzahlProps {
  DefaultAnzahl: number;
  MinAnzahl: number;
  MaxAnzahl: number;
}

export const defaultAnzahlProps = {
  DefaultAnzahl: 0,
  MinAnzahl: 0,
  MaxAnzahl: Number.MAX_SAFE_INTEGER,
};

export type AnzahlPropsMap = Record<string, AnzahlProps>;
export type ZubehörAnzahl = Record<string, number>;

export const getAnzahlPropsMap = Commons.cacheIdentity((auswahl: MAuswahl) =>
  Object.fromEntries(
    auswahl.Auswahlwert.map((auswahlwert) => {
      const key = auswahlwert.Key;
      const zubehoer = auswahlwert.Zubehoer?.[0] ?? defaultAnzahlProps;
      return [key, zubehoer];
    })
  )
) as (MAuswahl) => AnzahlPropsMap;

export const getDefaultAnzahl = Commons.cacheIdentity((auswahl: MAuswahl) => {
  const map = getAnzahlPropsMap(auswahl);
  const anzahl = {} as ZubehörAnzahl;
  Object.entries(map).forEach(([key, props]) => {
    anzahl[key] = props.DefaultAnzahl;
  });
  return anzahl;
});

/**
 * Zeigt Zubehör-Artikel basierend auf einer MAuswahl.
 *
 * Erwartet als externe Zustände lediglich:
 *  - mAuswahl: Datenquelle für Titel, Bilder und Namen
 *  - zubehör: Record, in dem die aktuellen Anzahlen pro Key liegen
 *  - minMax: Record, in dem Min-/Max-Anzahlen pro Key liegen
 */
export function Zusatzartikel({
  auswahl,
  zubehör,
  minMax,
  headerClassName,
  isKeyValid,
}: {
  auswahl?: MAuswahl;
  zubehör: ZubehörAnzahl;
  minMax?: AnzahlPropsMap;
  headerClassName?: string;
  isKeyValid?: (key: string) => boolean;
}) {
  // Defensive runtime guard: metadata can be temporarily inconsistent during updates.
  if (!auswahl?.Auswahlwert) {
    return null;
  }

  isKeyValid = isKeyValid ?? ((key: string) => true);
  const [, _force] = useReducer((c) => c + 1, 0);

  minMax = minMax ?? getAnzahlPropsMap(auswahl);
  const forceUpdate = () => {
    APP.dirty();
    _force();
  };

  return (
    <div className="zubehör-container" role="region" aria-labelledby="zubehoer-header">
      <div id="zubehoer-header" className={headerClassName ?? "header"}>
        <span>{auswahl.Text}</span>
      </div>

      {auswahl.Auswahlwert.filter(({ Key }) => isKeyValid(Key)).map((auswahlwert) => {
        const key = auswahlwert.Key;
        const { DefaultAnzahl, MinAnzahl, MaxAnzahl } = minMax[key] ?? defaultAnzahlProps;
        const value = zubehör[key] ?? DefaultAnzahl;

        return (
          <article className={"zubehör-number-input"} key={key}>
            <label htmlFor={key}>
              {auswahlwert?.Auswahlbild && <Image url={auswahlwert.Auswahlbild} />}
              <div className="title">
                <span
                  dangerouslySetInnerHTML={{
                    __html: auswahlwert.Name,
                  }}
                ></span>
              </div>
              <InfoIcon Text={auswahlwert?.Artikelinfo} />
            </label>

            <MinimalNumberInput
              id={key}
              ariaLabel={I18N("Anzahl Zusatzartikel") + " " + auswahlwert.Name}
              value={value}
              min={MinAnzahl}
              max={MaxAnzahl}
              onChange={(v) => {
                zubehör[key] = v;
                forceUpdate();
              }}
            />
          </article>
        );
      })}
    </div>
  );
}
